class VideoCarousel extends HTMLElement {
  static get observedAttributes() {
    return ['data-videos-per-page', 'data-video-aspect-ratio', 'data-card-background', 'data-card-text-color'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.currentPage = 1;
    this.isAnimating = false;
    this._isMobile = window.innerWidth <= 767;
    this._prevButton = null;
    this._nextButton = null;
    this._onPrevClick = null;
    this._onNextClick = null;
    this._onResize = null;
    this._initialized = false;
  }

  connectedCallback() {
    this.updateProperties();
    
    if ('IntersectionObserver' in window) {
      this._observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this._initialized) {
            this._initialized = true;
            this._observer.disconnect();
            this._init();
          }
        });
      }, { rootMargin: '200px' });
      this._observer.observe(this);
    } else {
      this._init();
    }
  }

  _init() {
    this.render();
    this.initEventListeners();
    this.showPage(1);
    this.updateNavButtons();
  }

  disconnectedCallback() {
    if (this._observer) {
      this._observer.disconnect();
    }
    if (this._prevButton && this._onPrevClick) {
      this._prevButton.removeEventListener('click', this._onPrevClick);
    }
    if (this._nextButton && this._onNextClick) {
      this._nextButton.removeEventListener('click', this._onNextClick);
    }
    if (this._onResize) {
      window.removeEventListener('resize', this._onResize);
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue) {
      const currentPage = this.currentPage;
      
      this.updateProperties();
      if (this._initialized) {
        this.render();
        this.initEventListeners();
      }
      this.currentPage = currentPage;
      this.updateNavButtons();
    }
  }

  updateProperties() {
    this.videosPerPage = parseInt(this.dataset.videosPerPage) || 4;
    this.videoAspectRatio = this.dataset.videoAspectRatio || '4-3';
    this.cardBackground = this.dataset.cardBackground || '255,255,255';
    this.cardTextColor = this.dataset.cardTextColor || '0,0,0';
  }

  _getMobileVideosPerPage() {
    return this._isMobile ? 1 : this.videosPerPage;
  }

  render() {
    const videos = [];
    for (let i = 0; i < this.children.length; i++) {
      const child = this.children[i];
      if (child.classList && child.classList.contains('video')) {
        videos.push(child);
      }
    }
    
    const mobileVideosPerPage = this._getMobileVideosPerPage();
    const totalPages = Math.ceil(videos.length / mobileVideosPerPage);
    
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          position: relative;
        }

        .carousel-container {
          position: relative;
          display: flex;
          align-items: center;
          gap: 20px;
          width: 100%;
        }

        .carousel-viewport {
          position: relative;
          flex: 1;
          overflow: hidden;
        }

        .carousel-track {
          display: flex;
          gap: 20px;
          position: relative;
          transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          width: calc(100% * ${totalPages});
        }

        .carousel-page {
          display: grid;
          grid-template-columns: repeat(${this.videosPerPage}, 1fr);
          gap: 20px;
          width: calc(100% / ${totalPages});
          flex-shrink: 0;
        }

        .video {
          overflow: hidden;
          display: flex;
          flex-direction: column;
          border-radius: 8px;
          background-color: rgb(${this.cardBackground});
        }

        @media (max-width: 767px) {
          .carousel-container {
            padding: 0;
            gap: 0;
            position: relative;
          }

          .carousel-viewport {
            overflow: hidden;
            width: 100%;
          }

          .carousel-track {
            display: flex;
            gap: 0;
            width: calc(100% * ${totalPages});
          }

          .carousel-page {
            display: flex;
            gap: 0;
            width: calc(100% / ${totalPages});
          }

          .video {
            flex: 0 0 100%;
            width: 100%;
            border-radius: 0;
          }

          .nav-button {
            display: flex;
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            z-index: 10;
            background: rgba(255, 255, 255, 0.9);
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
          }

          .nav-button--prev {
            left: 10px;
          }

          .nav-button--next {
            right: 10px;
          }
        }

        .video__sreenshot {
          height: 250px;
          background-color: #999;
          text-align: center;
          position: relative;
          overflow: hidden;
        }

        .video__sreenshot.aspect-4-3 {
          aspect-ratio: 4/3;
          height: auto;
        }

        .video__sreenshot.aspect-16-9 {
          aspect-ratio: 16/9;
          height: auto;
        }

        .video__sreenshot.aspect-1-1 {
          aspect-ratio: 1/1;
          height: auto;
        }

        .video__sreenshot.aspect-3-4 {
          aspect-ratio: 3/4;
          height: auto;
        }

        .video__cover {
          width: 100%;
          height: 100%;
          object-fit: cover;
          position: absolute;
          top: 0;
          left: 0;
          transition: opacity 0.3s ease;
        }

        .video__cover.hidden {
          opacity: 0;
          pointer-events: none;
        }

        .video__player {
          width: 100%;
          height: 100%;
          position: absolute;
          top: 0;
          left: 0;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.3s ease;
        }

        .video__player.active {
          opacity: 1;
          pointer-events: auto;
        }

        .video__player iframe,
        .video__player video {
          width: 100%;
          height: 100%;
          border: none;
        }

        .video__play-btn {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: none;
          border: none;
          cursor: pointer;
          z-index: 1;
          transition: transform 0.2s ease, opacity 0.3s ease;
          padding: 0;
        }

        .video__play-btn:hover {
          transform: translate(-50%, -50%) scale(1.1);
        }

        .video__play-btn.hidden {
          opacity: 0;
          pointer-events: none;
        }

        .video__play-btn svg {
          display: block;
          filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.3));
        }

        .video__author {
          padding: 20px;
          background-color: rgb(${this.cardBackground});
          color: rgb(${this.cardTextColor});
          font-weight: 600;
          font-size: 16px;
        }

        .nav-button {
          background: var(--pagination-button-background);
          border: none;
          width: var(--pagination-button-size);
          height: var(--pagination-button-size);
          border-radius: var(--pagination-button-border-radius);
          cursor: pointer;
          z-index: 100;
          transition: var(--pagination-button-transition);
          padding: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--pagination-button-color);
          flex-shrink: 0;
          box-shadow: var(--pagination-button-shadow);
        }

        .nav-button:hover:not(:disabled) {
          background: var(--pagination-button-background-hover);
          color: var(--pagination-button-color);
          box-shadow: var(--pagination-button-shadow-hover);
          transform: translateY(-2px);
        }

        .nav-button:disabled {
          opacity: 0.3;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }

        .nav-button svg {
          width: 24px;
          height: 24px;
        }

        @media (max-width: 767px) {
          .carousel-container {
            padding: 0;
            gap: 0;
          }

          .carousel-track {
            display: flex;
            grid-template-columns: unset;
            gap: 0;
            overflow-x: auto;
            overflow-y: hidden;
            scroll-snap-type: x mandatory;
            -webkit-overflow-scrolling: touch;
            scroll-behavior: smooth;
          }

          .carousel-track::-webkit-scrollbar {
            display: none;
          }

          .carousel-track {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }

          .video {
            flex: 0 0 100vw;
            width: 100vw;
            border-radius: 0;
            scroll-snap-align: start;
          }
        }
      </style>
      <div class="carousel-container">
        <button class="nav-button nav-button--prev" aria-label="上一页">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <div class="carousel-viewport">
          <div class="carousel-track"></div>
        </div>
        <button class="nav-button nav-button--next" aria-label="下一页">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
    `;

    this.shadowRoot.host.style.setProperty('--videos-per-page', this.videosPerPage);
    this.shadowRoot.host.style.setProperty('--card-background', this.cardBackground);
    this.shadowRoot.host.style.setProperty('--card-text-color', this.cardTextColor);

    const track = this.shadowRoot.querySelector('.carousel-track');
    
    const pages = [];
    for (let i = 0; i < totalPages; i++) {
      const startIndex = i * mobileVideosPerPage;
      const endIndex = startIndex + mobileVideosPerPage;
      const pageVideos = videos.slice(startIndex, endIndex);
      pages.push(pageVideos);
    }
    
    pages.forEach((pageVideos, pageIndex) => {
      const pageElement = document.createElement('div');
      pageElement.className = 'carousel-page';
      
      pageVideos.forEach((video, index) => {
        const videoClone = video.cloneNode(true);
        const screenshot = videoClone.querySelector('.video__sreenshot');
        if (screenshot) {
          screenshot.classList.add(`aspect-${this.videoAspectRatio}`);
        }
        pageElement.appendChild(videoClone);
      });
      
      track.appendChild(pageElement);
    });
  }

  initEventListeners() {
    const prevButton = this.shadowRoot.querySelector('.nav-button--prev');
    const nextButton = this.shadowRoot.querySelector('.nav-button--next');
    const viewport = this.shadowRoot.querySelector('.carousel-viewport');
    const track = viewport.querySelector('.carousel-track');

    this._prevButton = prevButton;
    this._nextButton = nextButton;
    this._onPrevClick = () => this.prevPage();
    this._onNextClick = () => this.nextPage();

    prevButton.addEventListener('click', this._onPrevClick);
    nextButton.addEventListener('click', this._onNextClick);

    track.addEventListener('transitionend', () => {
      this.isAnimating = false;
    });

    this._onResize = () => this._handleResize();
    window.addEventListener('resize', this._onResize);

    const playButtons = track.querySelectorAll('.video__play-btn');
    playButtons.forEach(button => {
      button.addEventListener('click', (e) => this.handlePlayClick(e));
    });
  }

  _handleResize() {
    const isMobile = window.innerWidth <= 767;
    if (isMobile !== this._isMobile) {
      this._isMobile = isMobile;
      this.render();
      this.initEventListeners();
    }
  }

  handlePlayClick(e) {
    const button = e.currentTarget;
    const container = button.closest('.video__sreenshot');
    const cover = container.querySelector('.video__cover');
    const player = container.querySelector('.video__player');
    const iframe = player.tagName === 'IFRAME' ? player : player.querySelector('iframe');
    const video = player.querySelector('video');

    cover.classList.add('hidden');
    button.classList.add('hidden');
    player.classList.add('active');

    if (iframe && iframe.dataset.src) {
      let src = iframe.dataset.src;
      if (!src.includes('autoplay')) {
        src += (src.includes('?') ? '&' : '?') + 'autoplay=1';
      }

      const newIframe = document.createElement('iframe');
      newIframe.src = src;
      newIframe.frameBorder = iframe.frameBorder || '0';
      newIframe.allow = iframe.allow || 'autoplay; encrypted-media';
      newIframe.allowFullscreen = true;
      newIframe.className = iframe.className;
      newIframe.style.cssText = iframe.style.cssText;
      newIframe.style.width = '100%';
      newIframe.style.height = '100%';

      iframe.parentNode.replaceChild(newIframe, iframe);
    }

    if (video) {
      video.play();
    }
  }

  showPage(page) {
    if (this.isAnimating) return;

    const viewport = this.shadowRoot.querySelector('.carousel-viewport');
    const track = viewport.querySelector('.carousel-track');
    const videos = Array.from(track.querySelectorAll('.video'));
    const mobileVideosPerPage = this._getMobileVideosPerPage();
    const totalPages = Math.ceil(videos.length / mobileVideosPerPage);

    if (page < 1 || page > totalPages) return;

    this.isAnimating = true;
    
    const translateX = -(page - 1) * (100 / totalPages);
    track.style.transform = `translateX(${translateX}%)`;
    
    this.currentPage = page;
    
    setTimeout(() => {
      this.updateNavButtons();
      this.isAnimating = false;
    }, 500);
  }

  prevPage() {
    if (this.currentPage > 1) {
      this.showPage(this.currentPage - 1);
    }
  }

  nextPage() {
    const viewport = this.shadowRoot.querySelector('.carousel-viewport');
    const track = viewport.querySelector('.carousel-track');
    const videos = Array.from(track.querySelectorAll('.video'));
    const mobileVideosPerPage = this._getMobileVideosPerPage();
    const totalPages = Math.ceil(videos.length / mobileVideosPerPage);
    
    if (this.currentPage < totalPages) {
      this.showPage(this.currentPage + 1);
    }
  }

  updateNavButtons() {
    const prevButton = this.shadowRoot.querySelector('.nav-button--prev');
    const nextButton = this.shadowRoot.querySelector('.nav-button--next');
    const viewport = this.shadowRoot.querySelector('.carousel-viewport');
    
    if (!prevButton || !nextButton || !viewport) {
      return;
    }
    
    const track = viewport.querySelector('.carousel-track');
    if (!track) {
      return;
    }
    
    const videos = Array.from(track.querySelectorAll('.video'));
    const mobileVideosPerPage = this._getMobileVideosPerPage();
    const totalPages = Math.ceil(videos.length / mobileVideosPerPage);

    prevButton.disabled = this.currentPage === 1;
    nextButton.disabled = this.currentPage === totalPages;
  }
}

if (!customElements.get('video-carousel')) {
  customElements.define('video-carousel', VideoCarousel);
}